// scope-matrix.js — Step 2 Scope Matrix
// Extracted from script.js. Contains modifier definitions, state, render, HTML builders,
// event handlers, and syncMatrixToHiddenInputs for the Step 2 scope matrix.
//
// Dependencies:
//   state.js          — sites, hypervisorMigrationScopes, upgradeScope, expandedComponentRows
//   workload-defs.js  — veeamBackupForMicrosoft365WorkloadId, kastenK10WorkloadId,
//                       getJobWorkloadDef, stripWorkloadUnitSuffix
//   utils.js          — escapeHtml, normalizeToggleMigratedCount, isVb365ManagedWorkload, getVbfCloudProvider
//   helpers.js        — buildEngagementSettingsHtml, buildMatrixTheadHtml, getModifierDefs,
//                       getModifierBlankState, isNonVbrWorkload, syncProtectionTileVisuals,
//                       isSubCellNA
//   recalc.js         — scheduleRenderEstimate, collectUpgradeVbrRows, isModifierActive,
//                       buildArchitectureStateSnapshot, getGfChipIcon
//
// Window globals (bridged from script.js):
//   VEEAM_SERVER_TYPES, VEEAM_SERVER_TYPE_ICONS,
//   window.applyArchitectureState(), window.syncReplicationFields()

import { sites, hypervisorMigrationScopes, upgradeScope, expandedComponentRows, AppState } from './state.js';
import { veeamBackupForMicrosoft365WorkloadId, kastenK10WorkloadId,
         getJobWorkloadDef, getJobTypeDef, stripWorkloadUnitSuffix } from './workload-defs.js';
import { escapeHtml, normalizeToggleMigratedCount, isVb365ManagedWorkload, getVbfCloudProvider } from './utils.js';
import { buildEngagementSettingsHtml, buildMatrixTheadHtml, getModifierDefs, getModifierBlankState,
         syncProtectionTileVisuals,
         isSubCellNA } from './helpers.js';
import { scheduleRenderEstimate, collectUpgradeVbrRows, isModifierActive, buildArchitectureStateSnapshot,
         getGfChipIcon, UNMANAGED_MODIFIER_DEFS } from './recalc.js';

// ── Scope Matrix (Step 2) ─────────────────────────────────────────────────────

// ─── Modifier definitions ─────────────────────────────────────────────────────

// UNMANAGED_MODIFIER_DEFS imported from recalc.js
window.UNMANAGED_MODIFIER_DEFS = UNMANAGED_MODIFIER_DEFS; // bridge for helpers.js

/**
 * Modifier definitions for the Managed by Veeam group.
 * These columns are read-only — their state is derived automatically from the
 * Veeam Components group (HC / Upgrade ticks cascade down to all managed rows).
 */
const MANAGED_MODIFIER_DEFS = [
    {
        key: 'assessment',
        label: 'Health Check',
        subs: [
            { key: 'hc', label: 'Health Check' },
        ],
    },
    {
        key: 'upgrade',
        label: 'Upgrade',
        subs: [
            { key: 'upgrade', label: 'Upgrade' },
        ],
    },
];
window.MANAGED_MODIFIER_DEFS = MANAGED_MODIFIER_DEFS;

/**
 * Modifier definitions for the Veeam Components group.
 * The 'upgrade' modifier uses a single sub-column (linuxVsa) as the "perform upgrade"
 * flag for ALL component types — VBR, VB365, VBfAWS, VBfAz, VBfGC, and Kasten.
 * VSA HA for VBR is handled separately via the per-instance detail cards below the
 * matrix, so vsaHa is not needed here as a column.
 */
const COMPONENT_MODIFIER_DEFS = [
    {
        key: 'assessment',
        label: 'Health Check',
        subs: [
            { key: 'hc', label: 'Health Check' },
        ],
    },
    {
        key: 'upgrade',
        label: 'Upgrade',
        subs: [
            { key: 'linuxVsa', label: 'Upgrade' },
        ],
    },
];
window.COMPONENT_MODIFIER_DEFS = COMPONENT_MODIFIER_DEFS;

// ─── State ────────────────────────────────────────────────────────────────────

/**
 * Per-workload treatment selections for Step 2 scope matrix.
 *   managed[workloadType]  = { assessment: { hc, audit }, upgrade: { vbr, proxy, repo } }
 *   unmanaged[workloadId]  = { protection: { backup, backupCopy, replication, verification, malware },
 *                              orchestration: { vro }, operations: { vone } }
 *   components[serverType]  = { assessment: { hc, audit }, upgrade: { vbr, proxy, repo } }
 */
let step2MatrixState = { managed: {}, unmanaged: {}, components: {}, migrationTargets: {}, serverManaged: {} };
window.step2MatrixState = step2MatrixState;

/**
 * Which modifier columns are currently expanded (per group).
 * Not persisted — kept across data-change re-renders because it lives outside renderScopeMatrix.
 */
const matrixExpandedCols = { managed: new Set(), unmanaged: new Set(), components: new Set(), migration: new Set(), server: new Set() };

// ─── N/A rules ────────────────────────────────────────────────────────────────

/**
 * Workload types that use Universal CDP (rather than Standard CDP for VMware vSphere).
 * Standard CDP supports VMware as both source and target.
 * Universal CDP supports these additional source types — replicating to a VMware target:
 *   Hyper-V VMs, Windows agents (Server 2016/2019/2022/2025), and cloud VMs running Windows.
 * Used to render the asterisk (*) on active CDP cells in the expanded matrix view.
 */
const CDP_UNIVERSAL_WORKLOAD_TYPES = new Set([
    'hyperv',                    // Hyper-V VMs
    'nutanix',                   // Nutanix AHV VMs running Windows
    'hpe-morpheus-vm-essentials',// HPE Morpheus VM Essentials VMs running Windows
    'vmware-cloud-director',     // VMware Cloud Director VMs running Windows
    'proxmox',                   // Proxmox VE VMs running Windows
    'ovirt',                     // oVirt VMs running Windows
    'kvm',                       // KVM VMs running Windows
    'scale-computing-hypercore', // Scale Computing HyperCore VMs running Windows
    'agent-windows',             // Physical/VM Windows Server 2016 / 2019 / 2022 / 2025
    'aws-ec2',                   // AWS EC2 instances running Windows
    'azure-vm',                  // Microsoft Azure VMs running Windows
    'gcp-vm',                    // Google Cloud VMs running Windows
]);

const _MATRIX_NA_SETS = {
    malware: new Set([
        'm365-exchange', 'm365-sharepoint', 'm365-teams', 'm365-onedrive',
        'kubernetes',
    ]),
    // linuxVsa is the "perform upgrade" sub-key for all component types — no N/A entries.
    // vsaHa (VSA HA) is VBR-only and is handled via per-instance cards, not column defs;
    // keeping the set populated prevents it from appearing if ever re-added as a column.
    linuxVsa: new Set(),
    vsaHa:    new Set(['vbfaws', 'vbfgc', 'vbfaz', 'vb365', 'kasten']),
};
window._MATRIX_NA_SETS = _MATRIX_NA_SETS;

/**
 * Allowlist sets — when a sub-key appears here, only the listed workload types
 * are supported; everything else is treated as N/A.
 * Use this instead of a blocklist when the supported set is small and stable.
 */
const _MATRIX_ALLOWLIST_SETS = {
    // VBR replication supports VMware vSphere and Microsoft Hyper-V only.
    replication: new Set(['vmware', 'hyperv']),
    // CDP supports VMware vSphere (Standard CDP) + Universal CDP workload types only.
    cdpReplication: new Set(['vmware', ...CDP_UNIVERSAL_WORKLOAD_TYPES]),
    // VRO supports VMware vSphere, Hyper-V, and physical (agent) workloads only.
    vro: new Set(['vmware', 'hyperv', 'agent-windows', 'agent-linux-kernel', 'agent-linux-nosnap']),
    // Verification: SureBackup on VMware/Hyper-V; SureBackup Lite on Nutanix; N/A elsewhere.
    verification: new Set(['vmware', 'hyperv', 'nutanix']),
};
window._MATRIX_ALLOWLIST_SETS = _MATRIX_ALLOWLIST_SETS;

/** Returns true when a sub-column key is N/A for the given workload type. */

/** Legacy compat shim — kept for any external callers. */
function isMatrixCellNA(treatment, workloadType) {
    return isSubCellNA(treatment, workloadType);
}

// ─── SVG constants ────────────────────────────────────────────────────────────

const MATRIX_CHECK_SVG    = `<svg width="12" height="12" viewBox="0 0 10 10" fill="none"><path d="M1.5 5l2.5 2.5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const MATRIX_PARTIAL_SVG  = `<svg width="12" height="12" viewBox="0 0 10 10" fill="none"><path d="M1.5 5l2.5 2.5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const MATRIX_LOCK_SVG     = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`;
const MATRIX_EXPAND_SVG      = `<svg width="9" height="9" viewBox="0 0 9 9" fill="none"><path d="M2.5 1.5l4 3-4 3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
window.MATRIX_EXPAND_SVG = MATRIX_EXPAND_SVG;
const MATRIX_COLLAPSE_SVG    = `<svg width="9" height="9" viewBox="0 0 9 9" fill="none"><path d="M6.5 1.5l-4 3 4 3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
window.MATRIX_COLLAPSE_SVG = MATRIX_COLLAPSE_SVG;
// Row-level expand/collapse chevrons (pointing down / up)
const MATRIX_ROW_EXPAND_SVG  = `<svg width="9" height="9" viewBox="0 0 9 9" fill="none"><path d="M1.5 2.5l3 4 3-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const MATRIX_ROW_COLLAPSE_SVG = `<svg width="9" height="9" viewBox="0 0 9 9" fill="none"><path d="M1.5 6.5l3-4 3 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Returns the modifier definitions array for the given group ('managed'|'unmanaged'|'components'). */

/**
 * Returns true if a modifier is "active" (any non-NA sub is on).
 * @param {object} modState    - sub-key state object
 * @param {object} modDef      - modifier definition (with .subs array)
 * @param {string} workloadType
 */

/**
 * Returns true if an unprotected workload has at least one active modifier
 * (protection, operations, or orchestration sub-cell) in the Step 2 matrix.
 * Workloads with no selections at all are treated as out-of-scope and excluded
 * from all Step 3 effort calculations.
 */

/**
 * Returns { active, total } counts for non-NA subs within a modifier.
 */
function countActiveSubs(modState, modDef, workloadType) {
    let active = 0, total = 0;
    modDef.subs.forEach((sub) => {
        if (isSubCellNA(sub.key, workloadType)) return;
        total++;
        if (modState && modState[sub.key]) active++;
    });
    return { active, total };
}

/**
 * Compute smart-default sub-states when a top-level modifier is turned ON.
 */
function getModifierDefaults(modKey, workloadType) {
    switch (modKey) {
        case 'protection':
            return {
                backup:          true,
                backupCopy:      false,
                replication:     !isSubCellNA('replication', workloadType),
                cdpReplication:  false,
                verification:    false,
                malware:         false,
            };
        case 'orchestration':
            return { vro: true };
        case 'operations':
            // Default to out-of-box reporting/dashboards (covered by base Veeam ONE
            // deployment — no extra effort). Custom reports/alarms/dashboards are opt-in
            // and only added when the customer needs tailored content.
            return { outOfBox: true, customReports: false, customAlarms: false, customDashboards: false };
        case 'assessment':
            return { hc: true };
        case 'upgrade':
            return { linuxVsa: true, vsaHa: false };
        default:
            return {};
    }
}

/** Build a blank (all-false) sub-state object for a modifier definition. */

/** Initialise a fresh row state for a given group and workload type. */
function initRowState(group, workloadType) {
    const defs = getModifierDefs(group);
    const row = {};
    defs.forEach((mod) => {
        if ((group === 'unmanaged' || group === 'migration') && mod.key === 'protection') {
            row[mod.key] = getModifierDefaults('protection', workloadType);
        } else {
            row[mod.key] = getModifierBlankState(mod);
        }
    });
    return row;
}

// ─── Main render function ─────────────────────────────────────────────────────

/**
 * Derives the Managed-by-Veeam row state from Veeam Component selections.
 * HC and Upgrade ticks on a component cascade automatically to all managed
 * workload rows — the cells are read-only from the user's perspective.
 * Called from renderScopeMatrix() before building managed group HTML.
 */
function syncManagedFromComponents() {
    // VBR HC — reuses same per-instance vs type-level logic as syncMatrixToHiddenInputs
    const _vbrRows = collectUpgradeVbrRows();
    const _hasAnyPerInstVbr = _vbrRows.some((r) => upgradeScope.perVbr[r.id] !== undefined);
    const vbrHcActive = _vbrRows.length > 0 && (
        _hasAnyPerInstVbr
            ? _vbrRows.some((r) => upgradeScope.perVbr[r.id]?.hc === true)
            : step2MatrixState.components['vbr']?.assessment?.hc === true
    );
    const otherHcActive = Object.entries(step2MatrixState.components)
        .filter(([k]) => k !== 'vbr')
        .some(([, s]) => s.assessment?.hc === true);
    const anyHcActive = vbrHcActive || otherHcActive;

    // VBR Upgrade — linuxVsa is the "perform the upgrade" sub-key
    const vbrUpgradeActive = _vbrRows.some((r) => {
        const s = upgradeScope.perVbr[r.id];
        if (s !== undefined) return s.linuxVsa === true;
        return step2MatrixState.components['vbr']?.upgrade?.linuxVsa === true;
    });
    const otherUpgradeActive = Object.entries(step2MatrixState.components)
        .filter(([k]) => k !== 'vbr')
        .some(([, s]) => s.upgrade?.linuxVsa === true);
    const anyUpgradeActive = vbrUpgradeActive || otherUpgradeActive;

    // Push derived state into every managed row
    Object.keys(step2MatrixState.managed).forEach((wlType) => {
        if (!step2MatrixState.managed[wlType]) return;
        if (!step2MatrixState.managed[wlType].assessment) {
            step2MatrixState.managed[wlType].assessment = {};
        }
        if (!step2MatrixState.managed[wlType].upgrade) {
            step2MatrixState.managed[wlType].upgrade = {};
        }
        step2MatrixState.managed[wlType].assessment.hc      = anyHcActive;
        step2MatrixState.managed[wlType].upgrade.upgrade    = anyUpgradeActive;
    });
}

/**
 * Build the HTML for the "Migration Targets" scope matrix group.
 * One row per HVM scope with includeInScope === true, using the target platform and VM count.
 */
function buildMigrationTargetsGroupHtml(migrationScopes) {
    const group      = 'migration';
    const modDefs    = getModifierDefs(group);
    const expandedSet = matrixExpandedCols[group];

    const rowsHtml = migrationScopes.map((scope) => {
        const key = `migration-${scope.id}`;
        const syntheticWl = {
            id:           key,
            workloadType: scope.targetPlatform,
            count:        scope.targetVmCount,
            customLabel:  null
        };
        return buildMatrixTbodyRowHtml(group, key, scope.targetPlatform, null, syntheticWl, modDefs, expandedSet);
    }).join('');

    const coverageHtml = buildMatrixCoverageRow(group, modDefs, expandedSet, null, null, migrationScopes);

    return `
    <div class="scope-matrix-group scope-matrix-group--migration">
        <div class="scope-matrix-group-hdr">
            <span class="scope-matrix-group-dot" aria-hidden="true"></span>
            <span class="scope-matrix-group-title">Migration Targets</span>
            <span class="scope-matrix-group-desc">Target platform workloads — configure protection scope to deploy after cutover</span>
        </div>
        <div class="scope-matrix-table-wrap">
        <table class="scope-matrix-table">
            ${buildMatrixTheadHtml(group, modDefs, expandedSet)}
            <tbody>
                ${rowsHtml}
                ${coverageHtml}
            </tbody>
        </table>
        </div>
    </div>`;
}

/**
 * Handle a click on a per-instance VBR cell (HC / linuxVsa / vsaHa toggles).
 * Used by per-instance sub-rows in the Veeam Components group.
 * Mutates upgradeScope.perVbr[id] then re-renders the scope matrix.
 */
function handleUpgradeVbrCellToggle(e) {
    const btn   = e.currentTarget;
    const id    = btn.dataset.upgradeVbrId;
    const field = btn.dataset.upgradeVbrField;
    if (!id || !field) return;
    // Initialise instance state from type-level defaults if not yet set.
    // Use strict === true checks (not !== false) so that missing/blank type-level
    // state doesn't default linuxVsa to true — that would poison anyVbrUpgrade in
    // deriveEngagementType() and force 'upgrade' even when only HC is selected.
    if (!upgradeScope.perVbr[id]) {
        const typeLevelUpgrade = step2MatrixState.components['vbr']?.upgrade;
        const typeLevelHc      = step2MatrixState.components['vbr']?.assessment?.hc === true;
        upgradeScope.perVbr[id] = {
            hc:       typeLevelHc,
            linuxVsa: typeLevelUpgrade?.linuxVsa === true,
            vsaHa:    typeLevelUpgrade?.vsaHa === true,
        };
    }
    if (field === 'hc') {
        upgradeScope.perVbr[id].hc = !upgradeScope.perVbr[id].hc;
    } else if (field === 'linuxVsa') {
        upgradeScope.perVbr[id].linuxVsa = !upgradeScope.perVbr[id].linuxVsa;
        // Reset HA when switching to Windows (linuxVsa=false)
        if (!upgradeScope.perVbr[id].linuxVsa) {
            upgradeScope.perVbr[id].vsaHa = false;
        }
    } else if (field === 'vsaHa') {
        upgradeScope.perVbr[id].vsaHa = !upgradeScope.perVbr[id].vsaHa;
    }
    syncMatrixToHiddenInputs();
    renderScopeMatrix();
    scheduleRenderEstimate();
}

/**
 * Build and inject the scope matrix into #scope-matrix-host.
 * Called when entering Step 2, and when site workload data changes while on Step 2.
 */

/**
 * Builds the "Engagement Settings" card rendered at the bottom of #scope-matrix-host.
 * Hosts VBEM and Advanced RBAC — global toggles that apply across all workloads.
 * Uses `engagement-` prefixed input names to avoid ID collisions with the architecture card.
 */

function renderScopeMatrix() {
    const host = document.getElementById('scope-matrix-host');
    if (!host) return;

    // ── Collect managed rows (source workloads with jobs, grouped by workload type) ──
    const managedByType = {};
    sites.forEach((site) => {
        // Source workloads with jobs → currently protected by Veeam → managed section
        (Array.isArray(site.sourceWorkloads) ? site.sourceWorkloads : []).forEach((sw) => {
            if (!sw.workloadValue || !Array.isArray(sw.jobs) || sw.jobs.length === 0) return;
            if (!managedByType[sw.workloadValue]) {
                managedByType[sw.workloadValue] = { totalCount: 0, jobTypes: new Set() };
            }
            managedByType[sw.workloadValue].totalCount += Math.max(0, parseInt(sw.totalCount, 10) || 0);
            sw.jobs.forEach((j) => { if (j.type) managedByType[sw.workloadValue].jobTypes.add(j.type); });
        });
    });

    // ── Collect unmanaged rows ──
    // Source workloads (Phase 4) — adapted to the shape expected by the matrix:
    //   id, workloadType (= workloadValue), count (= sum of all group counts).
    // Only source workloads WITHOUT jobs are unprotected/unmanaged; those with jobs
    // are already protected by Veeam and land in the managed section above.
    // Legacy unprotectedWorkloads are no longer used here; removed in Phase 6.
    const allUnmanaged = sites.flatMap((s) =>
        (Array.isArray(s.sourceWorkloads) ? s.sourceWorkloads : [])
            .filter((sw) => !Array.isArray(sw.jobs) || sw.jobs.length === 0)
            .map((sw) => ({
                id:           sw.id,
                workloadType: sw.workloadValue,
                count:        Math.max(0, parseInt(sw.totalCount, 10) || 0),
            }))
    );

    // ── Collect component rows (Veeam infrastructure servers, grouped by type) ──
    // Checks both named veeamServers instances AND the legacy existingEnvironment
    // count fields. Legacy VBR count is only used when no named VBR instances exist
    // on the same site (syncExistingEnvironmentFromServers keeps them in sync otherwise,
    // so checking both would double-count).
    const componentsByType = {};
    const _addComp = (type, n) => {
        if (!VEEAM_SERVER_TYPES[type] || n <= 0) return;
        if (!componentsByType[type]) componentsByType[type] = { count: 0 };
        componentsByType[type].count += n;
    };
    sites.forEach((site) => {
        const namedServers = Array.isArray(site.veeamServers) ? site.veeamServers : [];
        // Named server instances (VBR, VB365, Kasten, VBfAWS, VBfGC, VBfAz)
        namedServers.forEach((server) => {
            if (!server.type || !VEEAM_SERVER_TYPES[server.type]) return;
            _addComp(server.type, 1);
        });
        // Legacy existingEnvironment.vbrServers — only used when no named VBR on this site
        const hasNamedVbr = namedServers.some((s) => s.type === 'vbr');
        if (!hasNamedVbr) {
            const legacyVbr = normalizeToggleMigratedCount(site.existingEnvironment?.vbrServers);
            _addComp('vbr', legacyVbr);
        }
        // VBEM and VeeamOne are tracked as count fields only (no named instances)
        _addComp('vbem', normalizeToggleMigratedCount(site.existingEnvironment?.vbemServers));
        _addComp('vone', normalizeToggleMigratedCount(site.existingEnvironment?.veeamOneServers));
    });

    // ── Auto-infer Veeam components from Step 1 workload types ───────────────
    // If VB365 / VBfAWS / VBfAz / VBfGC / Kasten workloads are present as EXISTING
    // protected workloads or canvas jobs (i.e. already managed by a deployed Veeam
    // product), automatically add the corresponding product to the Veeam Components
    // group so it can be selected for upgrade or health check.
    //
    // Unprotected / greenfield workloads are intentionally excluded — if a customer
    // has unprotected M365 workloads, there is no VB365 server in the environment
    // yet, so offering HC or upgrade for it would be incorrect.
    {
        const step1WorkloadTypes = new Set([
            // Source workloads with jobs = existing Veeam-managed workloads → include for product inference
            ...sites.flatMap((s) => (Array.isArray(s.sourceWorkloads) ? s.sourceWorkloads : [])
                .filter((sw) => Array.isArray(sw.jobs) && sw.jobs.length > 0)
                .map((sw) => sw.workloadValue).filter(Boolean)),
            // unprotectedWorkloads / source workloads without jobs intentionally omitted — no existing product to HC/upgrade
        ]);
        const inferenceRules = [
            { test: (wt) => wt === veeamBackupForMicrosoft365WorkloadId, type: 'vb365'  },
            { test: (wt) => wt === kastenK10WorkloadId,                  type: 'kasten' },
            { test: (wt) => String(wt).startsWith('aws-'),               type: 'vbfaws' },
            { test: (wt) => String(wt).startsWith('azure-'),             type: 'vbfaz'  },
            { test: (wt) => String(wt).startsWith('gcp-'),               type: 'vbfgc'  },
        ];
        for (const { test, type } of inferenceRules) {
            if (!componentsByType[type] && [...step1WorkloadTypes].some(test)) {
                _addComp(type, 1);
            }
        }
    }

    // ── Collect named server instances with their mapped workload types ──────────
    // Use VEEAM_SERVER_TYPES as a bare global (same pattern as _addComp above) to
    // avoid the window. guard failing before the bridge is set.

    // Map sourceWorkload job type strings (camelCase internal) to jobTypeOptions values (lowercase).
    const _normJobType = (type) => {
        const map = { sureBackup: 'surebackup', sureReplica: 'surereplica', backupCopy: 'backup-copy', cdp: 'cdp-replication' };
        return map[type] || type || '';
    };

    // Normalise legacy 'ee-vbr-{slot}' managedByServerId values written before the
    // convention was standardised to 'env-vbr-{siteId}-{slot}'.
    const _normServerId = (id, siteId) => {
        if (!id) return id;
        const m = id.match(/^ee-vbr-(\d+)$/);
        return m ? `env-vbr-${siteId}-${m[1]}` : id;
    };

    // Build workloadsByType for a server: one row per job on sourceWorkloads linked to instanceId.
    // sitesToScan: array of sites to search for linked workloads.
    // VBR uses only the server's own site (instanceId is site-scoped: env-vbr-{siteId}-{slot}).
    // Named servers (VB365, VBfCloud, Kasten) use all sites because their IDs are globally unique
    // and workloads on any site may reference them.
    const _workloadsForServer = (sitesToScan, instanceId, isFallbackVbr = false) => {
        const result = {};
        sitesToScan.forEach((site) => {
            (Array.isArray(site.sourceWorkloads) ? site.sourceWorkloads : []).forEach((sw) => {
                if (!sw.workloadValue || !Array.isArray(sw.jobs) || sw.jobs.length === 0) return;
                sw.jobs.forEach((j) => {
                    const normId = _normServerId(j.managedByServerId, site.id);
                    const isExplicit = normId === instanceId;
                    // Fallback: first non-secondary VBR absorbs jobs with no linked server
                    const isFallback = isFallbackVbr && !j.managedByServerId;
                    if (!isExplicit && !isFallback) return;
                    // Key: swId + jobType so each job gets its own row (mirrors legacy job-per-row behaviour)
                    const key = `${sw.id}-${j.type || 'job'}`;
                    result[key] = {
                        totalCount:   Math.max(0, parseInt(j.count, 10) || 0),
                        poolCount:    Math.max(0, parseInt(sw.totalCount, 10) || 0),
                        workloadType: sw.workloadValue,
                        jobType:      _normJobType(j.type),
                    };
                });
            });
        });
        return result;
    };

    const namedServerInstances = [];
    sites.forEach((site) => {
        const siteServers = Array.isArray(site.veeamServers) ? site.veeamServers : [];
        siteServers.forEach((server) => {
            if (!VEEAM_SERVER_TYPES || !VEEAM_SERVER_TYPES[server.type]) return;
            // VBR instanceIds are site-scoped — only search the server's own site.
            // All other named server types (VB365, VBfCloud, Kasten) use globally unique IDs,
            // so search across all sites to find cross-site workload references.
            const sitesToScan = server.type === 'vbr' ? [site] : sites;
            const workloadsByType = _workloadsForServer(sitesToScan, server.instanceId);
            namedServerInstances.push({ server, site, workloadsByType });
        });
    });

    // ── Also collect synthetic VBR instances ─────────────────────────────────────
    // VBR is not stored in site.veeamServers — it is tracked as a count via
    // existingEnvironment.vbrServers. sourceWorkloads link to VBR using instanceIds
    // of the form 'env-vbr-{siteId}-{i}' (or legacy 'ee-vbr-{i}').
    sites.forEach((site) => {
        const hasNamedVbr = (Array.isArray(site.veeamServers) ? site.veeamServers : []).some((s) => s.type === 'vbr');
        if (hasNamedVbr) return; // named VBR instances were already collected above
        const vbrCount = normalizeToggleMigratedCount(site.existingEnvironment?.vbrServers);
        if (vbrCount === 0) return;
        const haRoles     = site.existingEnvironmentVbrHaRoles   || {};
        const platforms   = site.existingEnvironmentVbrPlatforms || {};
        const customLabel = (site.existingEnvironmentCustomLabels || {})['vbrServers'] || '';
        const nonSecCount = Array.from({ length: vbrCount }, (_, i) => haRoles[i] || null)
            .filter((r) => r !== 'secondary').length;
        let ordinal = 0;
        for (let i = 0; i < vbrCount; i++) {
            if ((haRoles[i] || null) === 'secondary') continue;
            ordinal++;
            const haRole   = haRoles[i]   || null;
            const platform = platforms[i] || null;
            const isVsaHa  = haRole === 'primary' && platform === 'linux';
            const defaultLabel = isVsaHa ? 'VBR VSA HA' : 'VBR Server';
            const baseLabel    = customLabel || defaultLabel;
            const label        = nonSecCount === 1 ? baseLabel : `${baseLabel} ${ordinal}`;
            const instanceId   = `env-vbr-${site.id}-${i}`;
            const isFirstVbr   = ordinal === 1;
            const workloadsByType = _workloadsForServer([site], instanceId, isFirstVbr);
            namedServerInstances.push({
                server: { instanceId, type: 'vbr', name: label, isLegacyComponent: true },
                site,
                workloadsByType,
            });
        }
    });

    // ── Unassigned managed workloads (linked to no server on this site) ──────────
    const assignedInstanceIds = new Set(namedServerInstances.map((n) => n.server.instanceId));
    const unassignedManagedByType = {};
    sites.forEach((site) => {
        (Array.isArray(site.sourceWorkloads) ? site.sourceWorkloads : []).forEach((sw) => {
            if (!sw.workloadValue || !Array.isArray(sw.jobs) || sw.jobs.length === 0) return;
            const isAssigned = sw.jobs.some(
                (j) => j.managedByServerId && assignedInstanceIds.has(_normServerId(j.managedByServerId, site.id))
            );
            if (isAssigned) return;
            if (!unassignedManagedByType[sw.workloadValue]) {
                unassignedManagedByType[sw.workloadValue] = { totalCount: 0, jobTypes: new Set() };
            }
            unassignedManagedByType[sw.workloadValue].totalCount += Math.max(0, parseInt(sw.totalCount, 10) || 0);
            sw.jobs.forEach((j) => { if (j.type) unassignedManagedByType[sw.workloadValue].jobTypes.add(j.type); });
        });
    });

    // Sort named server instances: VBR → VBfCloud (aws/az/gc) → VB365 → Kasten → others
    const SERVER_TYPE_ORDER = ['vbr', 'vbfaws', 'vbfaz', 'vbfgc', 'vb365', 'kasten'];
    namedServerInstances.sort((a, b) => {
        const ai = SERVER_TYPE_ORDER.indexOf(a.server.type);
        const bi = SERVER_TYPE_ORDER.indexOf(b.server.type);
        const ao = ai === -1 ? 99 : ai;
        const bo = bi === -1 ? 99 : bi;
        if (ao !== bo) return ao - bo;
        // Within same type: sort by server label alphabetically
        return (a.server.name || '').localeCompare(b.server.name || '');
    });

    const hasManagedRows    = Object.keys(managedByType).length > 0;
    const hasUnmanagedRows  = allUnmanaged.length > 0;
    const hasComponentRows  = Object.keys(componentsByType).length > 0;
    const hasNamedServerInstances = namedServerInstances.length > 0;

    if (!hasManagedRows && !hasUnmanagedRows && !hasComponentRows && !hasNamedServerInstances) {
        // Still render engagement settings even when no workloads defined yet
        host.innerHTML = `<p class="scope-matrix-empty">Add workloads in Step 1 to define the scope of this engagement.</p>`
            + buildEngagementSettingsHtml();
        bindMatrixEvents(host);
        return;
    }

    // ── Initialise / prune matrix state ──────────────────────────────────────

    // Managed: init new entries, prune removed
    Object.keys(managedByType).forEach((wlType) => {
        if (!step2MatrixState.managed[wlType]) {
            step2MatrixState.managed[wlType] = initRowState('managed', wlType);
        }
    });
    Object.keys(step2MatrixState.managed).forEach((wlType) => {
        if (!managedByType[wlType]) delete step2MatrixState.managed[wlType];
    });

    // Unmanaged: init new entries, prune removed
    allUnmanaged.forEach((wl) => {
        const key = String(wl.id);
        if (!step2MatrixState.unmanaged[key]) {
            step2MatrixState.unmanaged[key] = initRowState('unmanaged', wl.workloadType);
        }
    });
    const activeUnmanagedIds = new Set(allUnmanaged.map((w) => String(w.id)));
    Object.keys(step2MatrixState.unmanaged).forEach((k) => {
        if (!activeUnmanagedIds.has(k)) delete step2MatrixState.unmanaged[k];
    });

    // Components: init new entries, prune removed
    Object.keys(componentsByType).forEach((typeKey) => {
        if (!step2MatrixState.components[typeKey]) {
            step2MatrixState.components[typeKey] = initRowState('components', typeKey);
        }
    });
    Object.keys(step2MatrixState.components).forEach((typeKey) => {
        if (!componentsByType[typeKey]) delete step2MatrixState.components[typeKey];
    });

    // ServerManaged: init/prune per named server instance
    {
        const activeInstanceIds = new Set(namedServerInstances.map((n) => n.server.instanceId));
        // Prune removed instances
        Object.keys(step2MatrixState.serverManaged).forEach((id) => {
            if (!activeInstanceIds.has(id)) delete step2MatrixState.serverManaged[id];
        });
        // Init new instances
        namedServerInstances.forEach(({ server, workloadsByType }) => {
            const id = server.instanceId;
            if (!step2MatrixState.serverManaged[id]) step2MatrixState.serverManaged[id] = {};
            const sm = step2MatrixState.serverManaged[id];
            // _self for non-VBR servers
            if (server.type !== 'vbr' && !sm._self) {
                sm._self = { assessment: { hc: false }, upgrade: { linuxVsa: false } };
            }
            // Prune job keys no longer mapped
            Object.keys(sm).forEach((k) => {
                if (k !== '_self' && !workloadsByType[k]) delete sm[k];
            });
            // Init new job key entries; always refresh currentCount/poolCount from live Step 1 data
            Object.entries(workloadsByType).forEach(([jobKey, data]) => {
                if (!sm[jobKey]) sm[jobKey] = { assessment: { hc: false }, upgrade: { linuxVsa: false }, expansion: { enabled: false, targetCount: null } };
                if (!sm[jobKey].expansion) sm[jobKey].expansion = { enabled: false, targetCount: null };
                if (sm[jobKey].expansion.enabled === undefined) sm[jobKey].expansion.enabled = false;
                if (sm[jobKey].expansion.targetCount === undefined) sm[jobKey].expansion.targetCount = null;
                // Refresh read-only Step 1 values (may change if user edits Step 1)
                sm[jobKey].expansion.currentCount = data.totalCount;
                sm[jobKey].expansion.poolCount    = data.poolCount;
            });
            // Ensure VBR per-instance state exists
            if (server.type === 'vbr' && upgradeScope.perVbr[id] === undefined) {
                upgradeScope.perVbr[id] = { hc: false, linuxVsa: true, vsaHa: false };
            }
        });
    }

    // ── Collect migration target rows ───────────────────────────────────────
    const migrationTargetScopes = AppState.hvmEnabled
        ? hypervisorMigrationScopes.filter((s) => s.includeInScope)
        : [];

    // Init new migration target state entries; prune removed ones
    migrationTargetScopes.forEach((scope) => {
        const key = `migration-${scope.id}`;
        if (!step2MatrixState.migrationTargets[key]) {
            step2MatrixState.migrationTargets[key] = initRowState('migration', scope.targetPlatform);
        }
    });
    Object.keys(step2MatrixState.migrationTargets).forEach((k) => {
        if (!migrationTargetScopes.some((s) => `migration-${s.id}` === k)) {
            delete step2MatrixState.migrationTargets[k];
        }
    });
    const hasMigrationTargetRows = migrationTargetScopes.length > 0;

    // ── Build HTML ───────────────────────────────────────────────────────────
    let html = '';
    if (hasMigrationTargetRows)  html += buildMigrationTargetsGroupHtml(migrationTargetScopes);
    if (hasUnmanagedRows)        html += buildMatrixGroupHtml('unmanaged',  null,          allUnmanaged,  null);

    // Per-server-instance blocks — VBR instances share one consolidated table; others get individual blocks
    const vbrInstances    = namedServerInstances.filter((n) => n.server.type === 'vbr');
    const nonVbrInstances = namedServerInstances.filter((n) => n.server.type !== 'vbr');
    if (vbrInstances.length > 0)    html += buildVbrConsolidatedGroupHtml(vbrInstances);
    nonVbrInstances.forEach((inst) => { html += buildServerGroupHtml(inst); });

    // Flat fallback for unassigned managed workloads (no linked named server)
    const hasUnassignedManaged = Object.keys(unassignedManagedByType).length > 0;
    if (hasUnassignedManaged) html += buildMatrixGroupHtml('managed', unassignedManagedByType, null, null);

    // Components group — keep for vbem/vone and auto-inferred types not covered by named instances
    const namedTypes = new Set(namedServerInstances.map((n) => n.server.type));
    const remainingComponentsByType = Object.fromEntries(
        Object.entries(componentsByType)
            .filter(([type]) => !namedTypes.has(type))
            .sort(([a], [b]) => {
                const ai = SERVER_TYPE_ORDER.indexOf(a);
                const bi = SERVER_TYPE_ORDER.indexOf(b);
                return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
            })
    );
    if (Object.keys(remainingComponentsByType).length > 0) {
        html += buildMatrixGroupHtml('components', null, null, remainingComponentsByType);
    }

    // Verification footnote — shown when any workload has verification ticked.
    // Standard SureBackup = VMware/Hyper-V; SureBackup Lite = Nutanix.
    const hasStandardVerificationActive = [
        ...allUnmanaged.filter((wl) => step2MatrixState.unmanaged[String(wl.id)]?.protection?.verification && wl.workloadType !== 'nutanix'),
        ...migrationTargetScopes.filter((s) => step2MatrixState.migrationTargets[`migration-${s.id}`]?.protection?.verification && s.targetPlatform !== 'nutanix'),
    ].length > 0;
    const hasNutanixVerificationActive = [
        ...allUnmanaged.filter((wl) => step2MatrixState.unmanaged[String(wl.id)]?.protection?.verification && wl.workloadType === 'nutanix'),
        ...migrationTargetScopes.filter((s) => step2MatrixState.migrationTargets[`migration-${s.id}`]?.protection?.verification && s.targetPlatform === 'nutanix'),
    ].length > 0;
    const hasVerificationActive = hasStandardVerificationActive || hasNutanixVerificationActive;

    // CDP footnote — shown only when CDP is actually ticked for at least one workload.
    // Collect the workload types that have CDP active so we can show only the relevant note(s).
    const activeCdpWorkloadTypes = [
        ...allUnmanaged
            .filter((wl) => step2MatrixState.unmanaged[String(wl.id)]?.protection?.cdpReplication)
            .map((wl) => wl.workloadType),
        ...migrationTargetScopes
            .filter((s) => step2MatrixState.migrationTargets[`migration-${s.id}`]?.protection?.cdpReplication)
            .map((s) => s.targetPlatform),
    ];
    const hasStandardCdpActive  = activeCdpWorkloadTypes.includes('vmware');
    const hasUniversalCdpActive = activeCdpWorkloadTypes.some((t) => CDP_UNIVERSAL_WORKLOAD_TYPES.has(t));

    // Secondary Repository footnote — shown when backup copy is ticked for any Object Storage / NAS workload.
    const hasUnstructuredBackupCopyActive = allUnmanaged.some(
        (wl) => wl.workloadType === 'unstructured-data' && step2MatrixState.unmanaged[String(wl.id)]?.protection?.backupCopy
    );

    // Wrap all footnotes in a single group container so spacing between them is
    // controlled by flex gap rather than individual margins (avoids phantom spacing
    // between the Verification and CDP notes when both are visible).
    const footnoteItems = [];
    if (hasVerificationActive)                          footnoteItems.push(buildVerificationFootnoteHtml(hasStandardVerificationActive, hasNutanixVerificationActive));
    if (hasUnstructuredBackupCopyActive)                footnoteItems.push(buildSecondaryRepositoryFootnoteHtml());
    if (hasStandardCdpActive || hasUniversalCdpActive)  footnoteItems.push(buildCdpFootnoteHtml(hasStandardCdpActive, hasUniversalCdpActive));
    if (footnoteItems.length > 0) {
        html += `<div class="scope-matrix-notes-group">${footnoteItems.join('')}</div>`;
    }

    html += buildEngagementSettingsHtml();

    host.innerHTML = html;
    bindMatrixEvents(host);
}

// ─── HTML builders ────────────────────────────────────────────────────────────

/**
 * Build the Verification footnote shown below matrix group tables when any workload
 * has verification ticked.
 * @param {boolean} hasStandard - true when VMware or Hyper-V verification is active (full SureBackup)
 * @param {boolean} hasLite     - true when Nutanix verification is active (SureBackup Lite)
 */
function buildVerificationFootnoteHtml(hasStandard, hasLite) {
    const lines = [];
    if (hasStandard) {
        lines.push(`<span class="scope-matrix-footnote-line"><strong>Verification:</strong> `
            + `The first 10 workloads will be configured with a SureBackup virtual lab as a template. `
            + `The customer or partner should add additional workloads to the SureBackup job as required.</span>`);
    }
    if (hasLite) {
        lines.push(`<span class="scope-matrix-footnote-line"><strong>SureBackup Lite:</strong> `
            + `Backup verification mode enables you to check backup data consistency and perform a backup content scan `
            + `without testing the backup for recoverability.</span>`);
    }
    return `<p class="scope-matrix-footnote">${lines.join('')}</p>`;
}

/**
 * Build the Secondary Repository footnote shown when backup copy is selected for
 * any Object Storage / NAS workload — explains secondary copy retention behaviour.
 */
function buildSecondaryRepositoryFootnoteHtml() {
    return `<p class="scope-matrix-footnote"><span class="scope-matrix-footnote-line"><strong>Secondary Repository:</strong> `
        + `Provides a secondary copy of the Primary Repository. It is possible to set a different retention `
        + `(by default it uses the same retention as the Primary Repository).</span></p>`;
}

/**
 * Build the CDP footnote shown below all matrix group tables.
 * Only the sentences relevant to the active CDP workload types are rendered.
 * @param {boolean} hasStandardCdp  - true when VMware vSphere CDP is active
 * @param {boolean} hasUniversalCdp - true when any Universal CDP workload type is active
 */
function buildCdpFootnoteHtml(hasStandardCdp, hasUniversalCdp) {
    const standardPart = hasStandardCdp
        ? `<span class="scope-matrix-footnote-line"><strong>CDP:</strong> <strong>Continuous Data Protection for VMware vSphere</strong>`
          + ` requires VMware vSphere as both source and replication target.</span>`
        : '';
    const universalPart = hasUniversalCdp
        ? `<span class="scope-matrix-footnote-line"><strong>Universal CDP</strong>`
          + ` configure to protect supported Windows OS machines to supported VMware vSphere target.</span>`
        : '';
    return `<p class="scope-matrix-footnote">${standardPart}${universalPart}</p>`;
}

/**
 * Build a complete scope matrix group (group header + table) HTML string.
 * @param {'managed'|'unmanaged'|'components'} group
 */
function buildMatrixGroupHtml(group, managedByType, allUnmanaged, componentsByType) {
    const isManaged    = group === 'managed';
    const isComponents = group === 'components';
    const modDefs      = getModifierDefs(group);
    const expandedSet  = matrixExpandedCols[group];
    const anyExpanded  = modDefs.some((m) => expandedSet.has(m.key));

    const title = isComponents ? 'Existing Veeam Components'
        : isManaged ? 'Workloads Protected by Veeam' : 'Unmanaged / New Deployment';
    const desc  = isComponents ? 'Veeam infrastructure — select health check and upgrade scope'
        : isManaged ? 'Already protected — select health check and upgrade scope'
        : 'Not yet protected — select which protections to deploy';
    const groupClass = isComponents ? 'scope-matrix-group--components'
        : isManaged ? 'scope-matrix-group--managed' : 'scope-matrix-group--unmanaged';

    const rowsHtml = isComponents
        ? Object.entries(componentsByType).map(([typeKey, data]) =>
            buildMatrixTbodyRowHtml(group, typeKey, typeKey, data, null, modDefs, expandedSet)).join('')
        : isManaged
        ? Object.entries(managedByType).map(([wlType, data]) =>
            buildMatrixTbodyRowHtml(group, wlType, wlType, data, null, modDefs, expandedSet)).join('')
        : allUnmanaged.map((wl) =>
            buildMatrixTbodyRowHtml(group, String(wl.id), wl.workloadType, null, wl, modDefs, expandedSet)).join('');

    const coverageHtml = buildMatrixCoverageRow(
        group, modDefs, expandedSet,
        (isManaged || isComponents) ? Object.keys(isComponents ? componentsByType : managedByType) : null,
        allUnmanaged
    );

    return `
    <div class="scope-matrix-group ${groupClass}">
        <div class="scope-matrix-group-hdr">
            <span class="scope-matrix-group-dot" aria-hidden="true"></span>
            <span class="scope-matrix-group-title">${title}</span>
            <span class="scope-matrix-group-desc">${desc}</span>
        </div>
        <div class="scope-matrix-table-wrap">
        <table class="scope-matrix-table">
            ${buildMatrixTheadHtml(group, modDefs, expandedSet)}
            <tbody>
                ${rowsHtml}
                ${coverageHtml}
            </tbody>
        </table>
        </div>
    </div>`;
}

/**
 * Build the <thead> HTML for a matrix group.
 *
 * Always a single <tr>. Collapsed modifiers = one th with label + expand btn.
 * Expanded modifiers = one th per sub-column; the first sub-th shows the
 * modifier name + collapse button as a small group tag above the sub-label.
 * This avoids rowspan/colspan rendering issues with table-layout:fixed.
 */

/**
 * Build a complete scope matrix group for a single named server instance.
 * Shows a server row (HC + Upgrade) and per-workload rows below it.
 * Workload rows are visually locked ("inherited") when the server row is ticked.
 */
function buildServerGroupHtml({ server, site, workloadsByType }) {
    const instanceId = server.instanceId;
    const isVbr = server.type === 'vbr';
    const serverTypeDef = (window.VEEAM_SERVER_TYPES && window.VEEAM_SERVER_TYPES[server.type]) || {};
    const serverLabel = server.name || serverTypeDef.fullLabel || serverTypeDef.label || server.type;
    const serverIcon = (window.VEEAM_SERVER_TYPE_ICONS && (window.VEEAM_SERVER_TYPE_ICONS[server.type] || window.VEEAM_SERVER_TYPE_ICONS.vbr)) || '';
    const siteName = site.name || '';

    // Server's own HC/Upgrade state
    const selfHc = isVbr
        ? (upgradeScope.perVbr[instanceId]?.hc === true)
        : (step2MatrixState.serverManaged[instanceId]?._self?.assessment?.hc === true);
    const selfUpgrade = isVbr
        ? (upgradeScope.perVbr[instanceId]?.linuxVsa !== false)
        : (step2MatrixState.serverManaged[instanceId]?._self?.upgrade?.linuxVsa === true);

    const serverRowHtml = buildServerSelfRowHtml(instanceId, server, serverLabel, serverIcon, selfHc, selfUpgrade, siteName);

    const _wlEntries = Object.entries(workloadsByType);
    const _groupPos  = computeSwGroupPositions(_wlEntries.map(([k]) => k));
    const workloadRowsHtml = _wlEntries.map(([wlType, data], idx) =>
        buildServerWorkloadRowHtml(instanceId, wlType, data, selfHc, selfUpgrade, _groupPos[idx])
    ).join('');

    const desc = (serverTypeDef.fullLabel || serverTypeDef.label || server.type) + ' — select health check and upgrade scope';
    const siteTag = siteName ? `<span class="scope-matrix-server-site-tag">${escapeHtml(siteName)}</span>` : '';

    return `
    <div class="scope-matrix-group scope-matrix-group--server">
        <div class="scope-matrix-group-hdr">
            <span class="scope-matrix-group-dot" aria-hidden="true"></span>
            <span class="scope-matrix-group-title">${escapeHtml(serverLabel)}${siteTag}</span>
            <span class="scope-matrix-group-desc">${escapeHtml(desc)}</span>
        </div>
        <div class="scope-matrix-table-wrap">
        <table class="scope-matrix-table">
            <thead><tr>
                <th class="scope-matrix-th scope-matrix-th--wl">Component</th>
                <th class="scope-matrix-th scope-matrix-th--mod scope-matrix-th--plain"><span class="scope-matrix-mod-label">Health Check</span></th>
                <th class="scope-matrix-th scope-matrix-th--mod scope-matrix-th--plain"><span class="scope-matrix-mod-label">Upgrade</span></th>
                <th class="scope-matrix-th scope-matrix-th--mod scope-matrix-th--plain scope-matrix-th--expansion"><span class="scope-matrix-mod-label">Scope Expansion</span></th>
            </tr></thead>
            <tbody>
                ${serverRowHtml}
                ${workloadRowsHtml}
            </tbody>
        </table>
        </div>
    </div>`;
}

/**
 * Build a single consolidated group block for all VBR instances.
 * Multiple VBR servers share one table — each server gets its own row with a site badge,
 * followed by its mapped workload sub-rows.
 */
function buildVbrConsolidatedGroupHtml(vbrInstances) {
    if (vbrInstances.length === 0) return '';

    const serverIcon = (window.VEEAM_SERVER_TYPE_ICONS && window.VEEAM_SERVER_TYPE_ICONS.vbr) || '';
    const showSiteBadge = vbrInstances.length > 1
        || (vbrInstances.length === 1 && vbrInstances[0].site.name);

    const rowsHtml = vbrInstances.map(({ server, site, workloadsByType }) => {
        const instanceId  = server.instanceId;
        const serverLabel = server.name || 'VBR Server';
        const siteName    = showSiteBadge ? (site.name || '') : '';
        const selfHc      = upgradeScope.perVbr[instanceId]?.hc === true;
        const selfUpgrade = upgradeScope.perVbr[instanceId]?.linuxVsa !== false;

        const selfRow    = buildServerSelfRowHtml(instanceId, server, serverLabel, serverIcon, selfHc, selfUpgrade, siteName);
        const _wlEntries = Object.entries(workloadsByType);
        const _groupPos  = computeSwGroupPositions(_wlEntries.map(([k]) => k));
        const wlRows     = _wlEntries.map(([wlType, data], idx) =>
            buildServerWorkloadRowHtml(instanceId, wlType, data, selfHc, selfUpgrade, _groupPos[idx])
        ).join('');

        return selfRow + wlRows;
    }).join('');

    return `
    <div class="scope-matrix-group scope-matrix-group--server">
        <div class="scope-matrix-group-hdr">
            <span class="scope-matrix-group-dot" aria-hidden="true"></span>
            <span class="scope-matrix-group-title">Veeam Backup &amp; Replication</span>
            <span class="scope-matrix-group-desc">Select health check and upgrade scope per VBR server</span>
        </div>
        <div class="scope-matrix-table-wrap">
        <table class="scope-matrix-table">
            <thead><tr>
                <th class="scope-matrix-th scope-matrix-th--wl">Component</th>
                <th class="scope-matrix-th scope-matrix-th--mod scope-matrix-th--plain"><span class="scope-matrix-mod-label">Health Check</span></th>
                <th class="scope-matrix-th scope-matrix-th--mod scope-matrix-th--plain"><span class="scope-matrix-mod-label">Upgrade</span></th>
                <th class="scope-matrix-th scope-matrix-th--mod scope-matrix-th--plain scope-matrix-th--expansion"><span class="scope-matrix-mod-label">Scope Expansion</span></th>
            </tr></thead>
            <tbody>
                ${rowsHtml}
            </tbody>
        </table>
        </div>
    </div>`;
}

/**
 * Build the server's own row (the named instance itself — HC + Upgrade toggles).
 */
function buildServerSelfRowHtml(instanceId, server, label, iconSvg, selfHc, selfUpgrade, siteName = '') {
    const safeId   = escapeHtml(instanceId);
    const safeType = escapeHtml(server.type);
    const siteTagHtml = siteName ? ` <span class="scope-matrix-server-site-tag">${escapeHtml(siteName)}</span>` : '';

    const hcCell = `<td class="scope-matrix-td">
        <button type="button"
            class="scope-matrix-cell${selfHc ? ' is-active' : ''}"
            data-matrix-group="server"
            data-matrix-server-id="${safeId}"
            data-matrix-server-type="${safeType}"
            data-matrix-wl="_self"
            data-matrix-mod="assessment"
            data-matrix-sub="hc"
            aria-pressed="${selfHc}"
            title="Health Check for ${escapeHtml(label)}">
            ${selfHc ? MATRIX_CHECK_SVG : ''}
        </button>
    </td>`;

    const upgradeCell = `<td class="scope-matrix-td">
        <button type="button"
            class="scope-matrix-cell${selfUpgrade ? ' is-active' : ''}"
            data-matrix-group="server"
            data-matrix-server-id="${safeId}"
            data-matrix-server-type="${safeType}"
            data-matrix-wl="_self"
            data-matrix-mod="upgrade"
            data-matrix-sub="linuxVsa"
            aria-pressed="${selfUpgrade}"
            title="Upgrade ${escapeHtml(label)}">
            ${selfUpgrade ? MATRIX_CHECK_SVG : ''}
        </button>
    </td>`;

    return `<tr class="scope-matrix-row scope-matrix-row--server-self">
        <td class="scope-matrix-td scope-matrix-td--wl">
            <div class="scope-matrix-wl-cell">
                <span class="scope-matrix-wl-icon" aria-hidden="true">${iconSvg}</span>
                <span class="scope-matrix-wl-label">${escapeHtml(label)}</span>${siteTagHtml}
            </div>
        </td>
        ${hcCell}
        ${upgradeCell}
        <td class="scope-matrix-td scope-matrix-td--na" aria-label="Not applicable"><span class="scope-matrix-na">—</span></td>
    </tr>`;
}

/**
 * Given an ordered array of workloadsByType keys (format: "{swId}-{jobType}"),
 * returns a parallel array of group positions for the vertical grouping bar.
 * Single-row groups get 'only'; multi-row groups get 'start'/'mid'/'end'.
 */
function computeSwGroupPositions(keys) {
    const swIds = keys.map((k) => (k.match(/^(\d+)/) || ['', ''])[1]);
    return keys.map((_, i) => {
        const swId = swIds[i];
        if (!swId) return 'none';
        const first = swIds.indexOf(swId);
        const last  = swIds.lastIndexOf(swId);
        if (first === last) return 'only';
        if (i === first)    return 'start';
        if (i === last)     return 'end';
        return 'mid';
    });
}

/**
 * Build a workload row beneath a server block.
 * When the parent server has HC or Upgrade ticked, the row is inherited (locked).
 * @param {string} groupPos  'start'|'mid'|'end'|'only'|'none' — visual grouping bar position
 */
function buildServerWorkloadRowHtml(instanceId, jobKey, data, serverHcOn, serverUpgradeOn, groupPos = 'none') {
    const safeId     = escapeHtml(instanceId);
    const wlType     = data.workloadType;
    const jobType    = data.jobType || '';
    const ownState   = step2MatrixState.serverManaged[instanceId]?.[jobKey] || {};
    const ownHc      = ownState.assessment?.hc === true;

    const hcActive      = serverHcOn || ownHc;
    const upgradeActive = serverUpgradeOn;   // upgrade always mirrors the server — never independently toggled
    const hcInherited      = serverHcOn;
    const upgradeInherited = true;           // upgrade is always inherited; the server row is the only control

    const wlDef      = getJobWorkloadDef(wlType);
    const unit       = wlDef?.unit || '';
    const label      = wlDef?.matrixLabel || stripWorkloadUnitSuffix(wlDef?.label || wlType, unit);
    const jobTypeDef = getJobTypeDef(jobType);
    const jobLabel   = jobTypeDef?.label || '';
    const iconSvg    = getGfChipIcon(wlType) || '';
    const count      = data.totalCount || 0;

    const hcCell = `<td class="scope-matrix-td">
        <button type="button"
            class="scope-matrix-cell${hcActive ? ' is-active' : ''}${hcInherited ? ' is-inherited' : ''}"
            data-matrix-group="server"
            data-matrix-server-id="${safeId}"
            data-matrix-wl="${escapeHtml(jobKey)}"
            data-matrix-mod="assessment"
            data-matrix-sub="hc"
            ${hcInherited ? 'aria-disabled="true"' : `aria-pressed="${hcActive}"`}
            title="${hcInherited ? 'Inherited from server' : 'Health Check'}">
            ${hcActive ? (hcInherited ? MATRIX_LOCK_SVG : MATRIX_CHECK_SVG) : ''}
        </button>
    </td>`;

    const upgradeCell = `<td class="scope-matrix-td">
        <button type="button"
            class="scope-matrix-cell${upgradeActive ? ' is-active' : ''}${upgradeInherited ? ' is-inherited' : ''}"
            data-matrix-group="server"
            data-matrix-server-id="${safeId}"
            data-matrix-wl="${escapeHtml(jobKey)}"
            data-matrix-mod="upgrade"
            data-matrix-sub="linuxVsa"
            ${upgradeInherited ? 'aria-disabled="true"' : `aria-pressed="${upgradeActive}"`}
            title="${upgradeInherited ? 'Inherited from server' : 'Upgrade'}">
            ${upgradeActive ? (upgradeInherited ? MATRIX_LOCK_SVG : MATRIX_CHECK_SVG) : ''}
        </button>
    </td>`;

    // Scope Expansion column — only available when job doesn't already cover the full pool
    const expansionState = step2MatrixState.serverManaged[instanceId]?.[jobKey]?.expansion || {};
    const poolCount    = data.poolCount || 0;
    const currentCount = data.totalCount || 0;
    const canExpand    = poolCount > currentCount;
    const expEnabled   = expansionState.enabled === true;
    const targetCount  = expansionState.targetCount ?? poolCount;

    let expansionCell;
    if (!canExpand) {
        expansionCell = `<td class="scope-matrix-td scope-matrix-td--na" aria-label="Not applicable"><span class="scope-matrix-na">—</span></td>`;
    } else {
        const btnClass = `scope-matrix-cell${expEnabled ? ' is-active' : ''}`;
        const qtyHtml  = expEnabled ? `
            <input type="text" inputmode="numeric" pattern="[0-9]*"
                class="scope-matrix-expansion-qty"
                data-matrix-expansion="${escapeHtml(instanceId)}"
                data-matrix-expansion-key="${escapeHtml(jobKey)}"
                data-expansion-min="${currentCount}"
                data-expansion-max="${poolCount}"
                value="${targetCount}"
                title="Target: ${currentCount} existing → up to ${poolCount}"
            />` : '';
        expansionCell = `<td class="scope-matrix-td scope-matrix-td--expansion${expEnabled ? ' is-expansion-active' : ''}">
            <div class="scope-matrix-expansion-cell">
                <button type="button"
                    class="${btnClass}"
                    data-matrix-group="server"
                    data-matrix-server-id="${escapeHtml(instanceId)}"
                    data-matrix-server-type="${escapeHtml(data.workloadType)}"
                    data-matrix-wl="${escapeHtml(jobKey)}"
                    data-matrix-mod="expansion"
                    data-matrix-sub="enabled"
                    aria-pressed="${expEnabled}"
                    title="Expand scope of this job">
                    ${expEnabled ? MATRIX_CHECK_SVG : ''}
                </button>
                ${qtyHtml}
            </div>
        </td>`;
    }

    const groupClass = (groupPos === 'start' || groupPos === 'mid' || groupPos === 'end')
        ? ` is-group-${groupPos}` : '';
    return `<tr class="scope-matrix-row scope-matrix-row--server-workload${groupClass}">
        <td class="scope-matrix-td scope-matrix-td--wl">
            <div class="scope-matrix-wl-cell scope-matrix-wl-cell--server-workload">
                <span class="scope-matrix-wl-icon" aria-hidden="true">${iconSvg}</span>
                <span class="scope-matrix-wl-label">${escapeHtml(label)}</span>
                ${jobLabel ? `<span class="scope-matrix-wl-jobtag">${escapeHtml(jobLabel)}</span>` : ''}
                ${count > 0 ? `<span class="scope-matrix-wl-count">×${count}</span>` : ''}
            </div>
        </td>
        ${hcCell}
        ${upgradeCell}
        ${expansionCell}
    </tr>`;
}

/**
 * Build a single <tr> for a workload row.
 * @param {'managed'|'unmanaged'} group
 * @param {string} rowKey        - state key (wlType for managed, String(wl.id) for unmanaged)
 * @param {string} workloadType  - actual workload type string for N/A checks
 * @param {object|null} managedData - { totalCount } (managed rows only)
 * @param {object|null} wl       - unprotected workload object (unmanaged rows only)
 * @param {Array}  modDefs
 * @param {Set}    expandedSet
 */
function buildMatrixTbodyRowHtml(group, rowKey, workloadType, managedData, wl, modDefs, expandedSet) {
    let label, iconSvg, count;
    if (group === 'components') {
        const compDef = VEEAM_SERVER_TYPES[workloadType] || {};
        label   = compDef.label || workloadType;
        iconSvg = VEEAM_SERVER_TYPE_ICONS[workloadType] || VEEAM_SERVER_TYPE_ICONS.vbr;
        count   = managedData?.count || 0;
    } else {
        const wlDef = getJobWorkloadDef(workloadType);
        const unit  = wlDef?.unit || '';
        label   = wl
            ? (wl.customLabel || wlDef?.matrixLabel || stripWorkloadUnitSuffix(wlDef?.label || workloadType, unit))
            : (wlDef?.matrixLabel || stripWorkloadUnitSuffix(wlDef?.label || workloadType, unit));
        iconSvg = getGfChipIcon(workloadType) || '';
        count   = wl ? (wl.count || 0) : (managedData?.totalCount || 0);
    }

    const rowState = (group === 'managed'
        ? step2MatrixState.managed[rowKey]
        : group === 'components'
        ? step2MatrixState.components[rowKey]
        : group === 'migration'
        ? step2MatrixState.migrationTargets[rowKey]
        : step2MatrixState.unmanaged[rowKey]) || {};

    let cellsHtml = '';
    modDefs.forEach((mod) => {
        const isExpanded = expandedSet.has(mod.key);
        const modState   = rowState[mod.key] || {};

        if (isExpanded) {
            // Individual sub-cells
            mod.subs.forEach((sub, subIdx) => {
                const isNA     = isSubCellNA(sub.key, workloadType);
                const isActive = !isNA && !!modState[sub.key];
                // Universal CDP asterisk: shown when CDP is active on a Universal CDP workload type
                const isCdpUniversal    = sub.key === 'cdpReplication' && isActive && CDP_UNIVERSAL_WORKLOAD_TYPES.has(workloadType);
                // SureBackup Lite indicator: Nutanix uses Lite mode, not full SureBackup
                const isSureBackupLite  = sub.key === 'verification' && workloadType === 'nutanix';
                const isSubLast         = subIdx === mod.subs.length - 1;
                const isAffinityLast    = sub.affinityGroup && !isSubLast && mod.subs[subIdx + 1]?.affinityGroup !== sub.affinityGroup;
                const affinityCls       = isAffinityLast ? ' is-affinity-last' : '';
                if (isNA) {
                    cellsHtml += `<td class="scope-matrix-td scope-matrix-td--na${affinityCls}" title="Not applicable for this workload type"><span class="scope-matrix-na" aria-label="N/A">—</span></td>`;
                } else {
                    cellsHtml += `<td class="scope-matrix-td${affinityCls}">
                        <button type="button"
                            class="scope-matrix-cell${isActive ? ' is-active' : ''}"
                            data-matrix-group="${group}"
                            data-matrix-wl="${rowKey}"
                            data-matrix-wl-type="${workloadType}"
                            data-matrix-mod="${mod.key}"
                            data-matrix-sub="${sub.key}"
                            aria-pressed="${isActive}"
                            title="${sub.label}${isSureBackupLite ? ' — SureBackup Lite (see note below)' : ''}${isCdpUniversal ? ' — Universal CDP (see note below)' : ''}">
                            ${isActive ? MATRIX_CHECK_SVG : ''}
                        </button>
                    </td>`;
                }
            });
        } else {
            // Collapsed modifier cell
            const { active, total } = countActiveSubs(modState, mod, workloadType);

            // If every sub is N/A for this workload type, render the N/A indicator
            // instead of a button (e.g. Orchestration on unsupported workloads).
            if (total === 0) {
                cellsHtml += `<td class="scope-matrix-td scope-matrix-td--na" title="Not applicable for this workload type"><span class="scope-matrix-na" aria-label="N/A">—</span></td>`;
            } else {
                const isActive  = active > 0;
                const isPartial = isActive && active < total;
                let cellClass = 'scope-matrix-cell';
                if (isActive)  cellClass += ' is-active';
                if (isPartial) cellClass += ' is-partial';

                let cellContent = '';
                if (isPartial)     cellContent = MATRIX_PARTIAL_SVG;
                else if (isActive) cellContent = MATRIX_CHECK_SVG;

                cellsHtml += `<td class="scope-matrix-td">
                    <button type="button"
                        class="${cellClass}"
                        data-matrix-group="${group}"
                        data-matrix-wl="${rowKey}"
                        data-matrix-wl-type="${workloadType}"
                        data-matrix-mod="${mod.key}"
                        aria-pressed="${isActive}"
                        title="${mod.label}">
                        ${cellContent}
                    </button>
                </td>`;
            }
        }
    });

    // VBR in the components group supports row-level expansion to per-instance sub-rows.
    const isVbrRow       = group === 'components' && workloadType === 'vbr';
    const vbrInstances   = isVbrRow ? collectUpgradeVbrRows() : [];
    const canExpand      = isVbrRow && vbrInstances.length > 0;
    const isRowExpanded  = canExpand && expandedComponentRows.has('vbr');

    const wlCellContent = canExpand
        ? `<div class="scope-matrix-wl-cell${isRowExpanded ? ' is-expanded-parent' : ''}">
                <button type="button" class="scope-matrix-row-expand-btn"
                    data-matrix-row-expand="vbr"
                    aria-label="${isRowExpanded ? 'Collapse VBR instances' : 'Expand VBR instances'}"
                    title="${isRowExpanded ? 'Collapse VBR instances' : 'Expand per-instance detail'}">
                    ${isRowExpanded ? MATRIX_ROW_COLLAPSE_SVG : MATRIX_ROW_EXPAND_SVG}
                </button>
                <span class="scope-matrix-wl-icon" aria-hidden="true">${iconSvg}</span>
                <span class="scope-matrix-wl-label">${escapeHtml(label)}</span>
                ${count > 0 ? `<span class="scope-matrix-wl-count">×${count}</span>` : ''}
            </div>`
        : `<div class="scope-matrix-wl-cell">
                <span class="scope-matrix-wl-icon" aria-hidden="true">${iconSvg}</span>
                <span class="scope-matrix-wl-label">${escapeHtml(label)}</span>
                ${count > 0 ? `<span class="scope-matrix-wl-count">×${count}</span>` : ''}
            </div>`;

    const typeRow = `<tr class="scope-matrix-row${isRowExpanded ? ' scope-matrix-row--expanded-parent' : ''}">
        <td class="scope-matrix-td scope-matrix-td--wl">
            ${wlCellContent}
        </td>
        ${cellsHtml}
    </tr>`;

    // Append per-instance sub-rows beneath the VBR type row when expanded
    const instanceRows = isRowExpanded
        ? buildVbrInstanceSubRowsHtml(modDefs, expandedSet)
        : '';

    return typeRow + instanceRows;
}

/**
 * Build per-instance sub-rows for VBR when the row is expanded.
 * Each instance shows independent Assessment (HC) and Upgrade cells,
 * using upgradeScope.perVbr[id] for state (initialised from type-level on first expand).
 * @param {Array} modDefs    - COMPONENT_MODIFIER_DEFS
 * @param {Set}   expandedSet - matrixExpandedCols['components']
 */
function buildVbrInstanceSubRowsHtml(modDefs, expandedSet) {
    const vbrInstances = collectUpgradeVbrRows();
    return vbrInstances.map(({ id, name, siteLabel }) => {
        const instState = upgradeScope.perVbr[id] || { hc: false, linuxVsa: true, vsaHa: false };
        const safeId    = escapeHtml(id);
        const nameHtml  = siteLabel
            ? `${escapeHtml(name)}<span class="upgrade-vbr-site-tag">${escapeHtml(siteLabel)}</span>`
            : escapeHtml(name);

        let cellsHtml = '';
        modDefs.forEach((mod) => {
            if (mod.key === 'assessment') {
                // HC — single toggle (always "plain", no column expand)
                const isActive = instState.hc === true;
                cellsHtml += `<td class="scope-matrix-td">
                    <button type="button"
                        class="scope-matrix-cell${isActive ? ' is-active' : ''}"
                        data-upgrade-vbr-id="${safeId}"
                        data-upgrade-vbr-field="hc"
                        aria-pressed="${isActive}"
                        title="Health Check for ${escapeHtml(name)}">
                        ${isActive ? MATRIX_CHECK_SVG : ''}
                    </button>
                </td>`;
            } else if (mod.key === 'upgrade') {
                const isColExpanded = expandedSet.has('upgrade');
                const isLinux = instState.linuxVsa !== false;
                const isHa    = instState.vsaHa === true;
                if (!isColExpanded) {
                    // Collapsed: single "Upgrade" toggle (linuxVsa = upgrading)
                    cellsHtml += `<td class="scope-matrix-td">
                        <button type="button"
                            class="scope-matrix-cell${isLinux ? ' is-active' : ''}"
                            data-upgrade-vbr-id="${safeId}"
                            data-upgrade-vbr-field="linuxVsa"
                            aria-pressed="${isLinux}"
                            title="Upgrade ${escapeHtml(name)} to latest">
                            ${isLinux ? MATRIX_CHECK_SVG : ''}
                        </button>
                    </td>`;
                } else {
                    // Expanded: Linux (VSA) + VSA HA sub-columns
                    cellsHtml += `<td class="scope-matrix-td">
                        <button type="button"
                            class="scope-matrix-cell${isLinux ? ' is-active' : ''}"
                            data-upgrade-vbr-id="${safeId}"
                            data-upgrade-vbr-field="linuxVsa"
                            aria-pressed="${isLinux}"
                            title="Linux (VSA)">
                            ${isLinux ? MATRIX_CHECK_SVG : ''}
                        </button>
                    </td>
                    <td class="scope-matrix-td${!isLinux ? ' scope-matrix-td--na' : ''}">
                        ${isLinux
                            ? `<button type="button"
                                   class="scope-matrix-cell${isHa ? ' is-active' : ''}"
                                   data-upgrade-vbr-id="${safeId}"
                                   data-upgrade-vbr-field="vsaHa"
                                   aria-pressed="${isHa}"
                                   title="VSA HA">
                                   ${isHa ? MATRIX_CHECK_SVG : ''}
                               </button>`
                            : `<span class="scope-matrix-na" aria-label="N/A">—</span>`
                        }
                    </td>`;
                }
            }
        });

        return `<tr class="scope-matrix-row scope-matrix-row--instance">
            <td class="scope-matrix-td scope-matrix-td--wl scope-matrix-td--instance">
                <div class="scope-matrix-wl-cell scope-matrix-wl-cell--instance">
                    <span class="scope-matrix-wl-icon" aria-hidden="true">${VEEAM_SERVER_TYPE_ICONS.vbr}</span>
                    <span class="scope-matrix-wl-label">${nameHtml}</span>
                </div>
            </td>
            ${cellsHtml}
        </tr>`;
    }).join('');
}

/** Toggle row-level expansion for a component type (e.g., VBR instances). */
function handleMatrixRowExpand(e) {
    e.stopPropagation();
    const rowKey = e.currentTarget.dataset.matrixRowExpand;
    if (!rowKey) return;

    if (expandedComponentRows.has(rowKey)) {
        expandedComponentRows.delete(rowKey);
    } else {
        expandedComponentRows.add(rowKey);
        // Initialise per-instance state from type-level defaults for any uninitialised instance
        if (rowKey === 'vbr') {
            const typeLevelUpgrade = step2MatrixState.components['vbr']?.upgrade || { linuxVsa: true, vsaHa: false };
            const typeLevelHc = step2MatrixState.components['vbr']?.assessment?.hc === true;
            collectUpgradeVbrRows().forEach(({ id }) => {
                if (upgradeScope.perVbr[id] === undefined) {
                    upgradeScope.perVbr[id] = {
                        hc:       typeLevelHc,
                        linuxVsa: typeLevelUpgrade.linuxVsa !== false,
                        vsaHa:    typeLevelUpgrade.vsaHa === true,
                    };
                }
            });
        }
    }
    renderScopeMatrix();
}

/**
 * Build the coverage summary <tr> for a matrix group.
 * When a modifier is collapsed, counts per-modifier (workloads with ≥1 active sub).
 * When a modifier is expanded, counts per sub-column.
 * @param {string}     group
 * @param {Array}      modDefs
 * @param {Set}        expandedSet
 * @param {Array|null} keyedTypes   - type/wlType keys for managed/components groups
 * @param {Array|null} allUnmanaged - unmanaged workload objects
 */
function buildMatrixCoverageRow(group, modDefs, expandedSet, keyedTypes, allUnmanaged, migrationScopes) {
    const isManaged    = group === 'managed';
    const isComponents = group === 'components';
    const isMigration  = group === 'migration';
    const isKeyedGroup = isManaged || isComponents;
    const stateMap     = isComponents ? step2MatrixState.components : step2MatrixState.managed;
    let coverageCells = '';

    modDefs.forEach((mod) => {
        const isExpanded = expandedSet.has(mod.key);

        if (isExpanded) {
            // One coverage cell per sub
            mod.subs.forEach((sub, subIdx) => {
                let active = 0, possible = 0;
                if (isKeyedGroup) {
                    (keyedTypes || []).forEach((typeKey) => {
                        if (isSubCellNA(sub.key, typeKey)) return;
                        possible++;
                        if ((stateMap[typeKey]?.[mod.key] || {})[sub.key]) active++;
                    });
                } else if (isMigration) {
                    (migrationScopes || []).forEach((scope) => {
                        const k = `migration-${scope.id}`;
                        if (isSubCellNA(sub.key, scope.targetPlatform)) return;
                        possible++;
                        if ((step2MatrixState.migrationTargets[k]?.[mod.key] || {})[sub.key]) active++;
                    });
                } else {
                    (allUnmanaged || []).forEach((wl) => {
                        if (isSubCellNA(sub.key, wl.workloadType)) return;
                        possible++;
                        if ((step2MatrixState.unmanaged[String(wl.id)]?.[mod.key] || {})[sub.key]) active++;
                    });
                }
                const isSubLast      = subIdx === mod.subs.length - 1;
                const isAffinityLast = sub.affinityGroup && !isSubLast && mod.subs[subIdx + 1]?.affinityGroup !== sub.affinityGroup;
                const affinityCls    = isAffinityLast ? ' is-affinity-last' : '';
                coverageCells += `<td class="scope-matrix-td scope-matrix-coverage-cell${affinityCls}"><span>${active}</span><span class="scope-matrix-coverage-sep">/</span><span>${possible}</span></td>`;
            });
        } else {
            // One coverage cell for the whole modifier
            let active = 0, possible = 0;
            if (isKeyedGroup) {
                (keyedTypes || []).forEach((typeKey) => {
                    possible++;
                    if (isModifierActive(stateMap[typeKey]?.[mod.key], mod, typeKey)) active++;
                });
            } else if (isMigration) {
                (migrationScopes || []).forEach((scope) => {
                    const k = `migration-${scope.id}`;
                    possible++;
                    if (isModifierActive(step2MatrixState.migrationTargets[k]?.[mod.key], mod, scope.targetPlatform)) active++;
                });
            } else {
                (allUnmanaged || []).forEach((wl) => {
                    possible++;
                    if (isModifierActive(step2MatrixState.unmanaged[String(wl.id)]?.[mod.key], mod, wl.workloadType)) active++;
                });
            }
            coverageCells += `<td class="scope-matrix-td scope-matrix-coverage-cell"><span>${active}</span><span class="scope-matrix-coverage-sep">/</span><span>${possible}</span></td>`;
        }
    });

    return `<tr class="scope-matrix-row scope-matrix-row--coverage">
        <td class="scope-matrix-td scope-matrix-td--wl scope-matrix-coverage-label">Coverage</td>
        ${coverageCells}
    </tr>`;
}

// ─── Event handlers ───────────────────────────────────────────────────────────

/** Bind all matrix interaction events after rendering into host. */
function bindMatrixEvents(host) {
    // Expand/collapse triggers — th headers (role="button") and inner collapse buttons
    host.querySelectorAll('[data-matrix-expand]').forEach((el) => {
        el.addEventListener('click', handleMatrixColExpand);
        // Keyboard support for th[role="button"]
        if (el.tagName === 'TH') {
            el.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleMatrixColExpand(e); }
            });
        }
    });
    // Sub-toggle buttons (expanded cells — have data-matrix-sub)
    host.querySelectorAll('[data-matrix-sub]').forEach((btn) => {
        btn.addEventListener('click', handleMatrixSubToggle);
    });
    // Modifier-toggle buttons (collapsed cells — have data-matrix-mod but NOT data-matrix-sub and NOT data-matrix-expand)
    host.querySelectorAll('[data-matrix-mod]:not([data-matrix-sub]):not([data-matrix-expand])').forEach((btn) => {
        btn.addEventListener('click', handleMatrixModifierToggle);
    });
    // Row-level expand/collapse (VBR instances in Veeam Components)
    host.querySelectorAll('[data-matrix-row-expand]').forEach((btn) => {
        btn.addEventListener('click', handleMatrixRowExpand);
    });
    // Per-instance VBR cell toggles (HC / linuxVsa / vsaHa) in expanded sub-rows
    host.querySelectorAll('button[data-upgrade-vbr-field]').forEach((btn) => {
        btn.addEventListener('click', handleUpgradeVbrCellToggle);
    });

    // Scope expansion qty input — updates targetCount when expansion is enabled
    host.addEventListener('input', (e) => {
        const input = e.target.closest('[data-matrix-expansion]');
        if (!input) return;
        const instanceId = input.dataset.matrixExpansion;
        const jobKey     = input.dataset.matrixExpansionKey;
        const sm = step2MatrixState.serverManaged[instanceId];
        if (!sm?.[jobKey]?.expansion) return;
        const raw = parseInt(input.value, 10);
        const min = parseInt(input.dataset.expansionMin, 10) || 0;
        const max = parseInt(input.dataset.expansionMax, 10) || 0;
        const clamped = Number.isFinite(raw) ? Math.min(Math.max(raw, min), max) : max;
        sm[jobKey].expansion.targetCount = clamped;
        syncMatrixToHiddenInputs();
        scheduleRenderEstimate();
    });

    // Engagement settings — global toggles in the settings card
    host.addEventListener('change', (e) => {
        const target = e.target;
        if (!(target instanceof HTMLInputElement) || !target.dataset.engagementField) return;
        const field = target.dataset.engagementField;
        const value = target.value;
        if (field === 'engagement-vbem') {
            window.applyArchitectureState({ ...buildArchitectureStateSnapshot(), vbem: value },
                sites.length);
        } else if (field === 'engagement-advanced-rbac') {
            window.applyArchitectureState({ ...buildArchitectureStateSnapshot(), advancedRbac: value },
                sites.length);
        }
        scheduleRenderEstimate();
    });
}

/** Handle expand/collapse button click on a modifier column header. */
function handleMatrixColExpand(e) {
    e.stopPropagation();
    const btn    = e.currentTarget;
    const group  = btn.dataset.matrixGroup;
    const modKey = btn.dataset.matrixExpand;
    const expSet = matrixExpandedCols[group];
    if (expSet.has(modKey)) {
        expSet.delete(modKey);
    } else {
        expSet.add(modKey);
    }
    renderScopeMatrix();
}

/**
 * Handle a click on a collapsed modifier cell (top-level toggle).
 * Applies smart defaults when turning on; clears all subs when turning off.
 */
function handleMatrixModifierToggle(e) {
    const btn          = e.currentTarget;
    const group        = btn.dataset.matrixGroup;
    const rowKey       = btn.dataset.matrixWl;
    const workloadType = btn.dataset.matrixWlType;
    const modKey       = btn.dataset.matrixMod;

    const stateGroup = group === 'managed' ? step2MatrixState.managed
        : group === 'components' ? step2MatrixState.components
        : group === 'migration' ? step2MatrixState.migrationTargets
        : step2MatrixState.unmanaged;
    if (!stateGroup[rowKey]) stateGroup[rowKey] = initRowState(group, workloadType);

    const modDef = getModifierDefs(group).find((m) => m.key === modKey);
    if (!modDef) return;

    const modState          = stateGroup[rowKey][modKey] || {};
    const isCurrentlyActive = isModifierActive(modState, modDef, workloadType);

    if (isCurrentlyActive) {
        // Turn off: blank all subs
        stateGroup[rowKey][modKey] = getModifierBlankState(modDef);
    } else {
        // Turn on: apply smart defaults
        stateGroup[rowKey][modKey] = getModifierDefaults(modKey, workloadType);
    }

    syncMatrixToHiddenInputs();
    renderScopeMatrix();
    scheduleRenderEstimate();
}

/** Handle a click on an individual sub-column cell (when a modifier is expanded). */
function handleMatrixSubToggle(e) {
    const btn   = e.currentTarget;
    const group = btn.dataset.matrixGroup;

    // Delegate server group clicks to dedicated handler
    if (group === 'server') {
        handleServerMatrixSubToggle(e);
        return;
    }

    const rowKey       = btn.dataset.matrixWl;
    const workloadType = btn.dataset.matrixWlType;
    const modKey       = btn.dataset.matrixMod;
    const subKey       = btn.dataset.matrixSub;

    const stateGroup = group === 'managed' ? step2MatrixState.managed
        : group === 'components' ? step2MatrixState.components
        : group === 'migration' ? step2MatrixState.migrationTargets
        : step2MatrixState.unmanaged;
    if (!stateGroup[rowKey]) stateGroup[rowKey] = initRowState(group, workloadType);
    if (!stateGroup[rowKey][modKey]) {
        const modDef = getModifierDefs(group).find((m) => m.key === modKey);
        stateGroup[rowKey][modKey] = modDef ? getModifierBlankState(modDef) : {};
    }

    stateGroup[rowKey][modKey][subKey] = !stateGroup[rowKey][modKey][subKey];

    syncMatrixToHiddenInputs();
    renderScopeMatrix();
    scheduleRenderEstimate();
}

/**
 * Handle a click on a server-group matrix cell (server self-row or workload row).
 * Mutates upgradeScope.perVbr (for VBR) or step2MatrixState.serverManaged (for others).
 */
function handleServerMatrixSubToggle(e) {
    const btn        = e.currentTarget;
    const serverId   = btn.dataset.matrixServerId;
    const serverType = btn.dataset.matrixServerType;
    const rowKey     = btn.dataset.matrixWl;    // '_self' or workloadType
    const modKey     = btn.dataset.matrixMod;   // 'assessment' or 'upgrade'
    const subKey     = btn.dataset.matrixSub;   // 'hc' or 'linuxVsa'

    if (rowKey === '_self') {
        // Server's own toggle
        if (serverType === 'vbr') {
            if (!upgradeScope.perVbr[serverId]) {
                // Inherit type-level upgrade state if available; otherwise default upgrade to false.
                // Do NOT default linuxVsa to true here — that would poison anyVbrUpgrade in
                // deriveEngagementType(), causing the engagement to resolve as 'upgrade' even
                // when the user has only selected HC.
                const typeLevelUpgrade = step2MatrixState.components['vbr']?.upgrade;
                upgradeScope.perVbr[serverId] = {
                    hc:       false,
                    linuxVsa: typeLevelUpgrade?.linuxVsa === true,
                    vsaHa:    typeLevelUpgrade?.vsaHa === true,
                };
            }
            if (subKey === 'hc') {
                upgradeScope.perVbr[serverId].hc = !upgradeScope.perVbr[serverId].hc;
            } else if (subKey === 'linuxVsa') {
                upgradeScope.perVbr[serverId].linuxVsa = !upgradeScope.perVbr[serverId].linuxVsa;
                // Reset HA when switching to Windows
                if (!upgradeScope.perVbr[serverId].linuxVsa) upgradeScope.perVbr[serverId].vsaHa = false;
            }
        } else {
            const sm = step2MatrixState.serverManaged[serverId];
            if (!sm) return;
            if (!sm._self) sm._self = { assessment: { hc: false }, upgrade: { linuxVsa: false } };
            if (!sm._self[modKey]) sm._self[modKey] = {};
            sm._self[modKey][subKey] = !sm._self[modKey][subKey];
        }
    } else {
        // Workload row — block interaction if the value is inherited from server
        const isVbr = serverType === 'vbr';
        const serverHcOn = isVbr
            ? upgradeScope.perVbr[serverId]?.hc === true
            : step2MatrixState.serverManaged[serverId]?._self?.assessment?.hc === true;
        const serverUpgradeOn = isVbr
            ? upgradeScope.perVbr[serverId]?.linuxVsa !== false
            : step2MatrixState.serverManaged[serverId]?._self?.upgrade?.linuxVsa === true;

        if (subKey === 'hc' && serverHcOn) return;
        if (subKey === 'linuxVsa') return; // upgrade is always server-level — never per-workload

        const sm = step2MatrixState.serverManaged[serverId];
        if (!sm) return;
        if (!sm[rowKey]) sm[rowKey] = { assessment: { hc: false }, upgrade: { linuxVsa: false }, expansion: { enabled: false, targetCount: null } };
        if (!sm[rowKey][modKey]) sm[rowKey][modKey] = {};
        sm[rowKey][modKey][subKey] = !sm[rowKey][modKey][subKey];
    }

    syncMatrixToHiddenInputs();
    renderScopeMatrix();
    scheduleRenderEstimate();
}

// ─── Sync to hidden inputs ────────────────────────────────────────────────────

/**
 * Propagate scope matrix selections into the legacy hidden inputs
 * that the calculation engine reads, so estimates stay accurate.
 * DOM element refs are resolved inline to avoid dependency on script.js module-level consts.
 */
function syncMatrixToHiddenInputs() {
    // Resolve DOM element references inline (avoids dependency on script.js consts)
    const backupVerificationRequiredInput        = document.getElementById('backup-verification-required');
    const sureBackupRequiredInput                = document.getElementById('sure-backup-required');
    const replicationRequiredToggle              = document.getElementById('replication-required');
    const cdpReplicationRequiredToggle           = document.getElementById('cdp-replication-required');
    const tapeIntegrationRequiredToggle          = document.getElementById('tape-integration-required');
    const veeamOneRequiredToggle                 = document.getElementById('veeam-one-required');
    const upgradeHealthCheckReviewRequiredToggle = document.getElementById('upgrade-health-check-review-required');

    const unmanagedStates = [...Object.values(step2MatrixState.unmanaged), ...Object.values(step2MatrixState.migrationTargets)];

    // Backup → backup-verification-required
    const hasBackup = unmanagedStates.some((s) => s.protection?.backup);
    if (backupVerificationRequiredInput) {
        backupVerificationRequiredInput.value = hasBackup ? 'yes_limited' : 'no';
    }
    const bvNo  = document.getElementById('backup-verification-no');
    const bvYes = document.getElementById('backup-verification-yes');
    if (bvNo)  bvNo.checked  = !hasBackup;
    if (bvYes) bvYes.checked =  hasBackup;

    // Backup Copy → #backup-copy-required checkbox
    const hasBackupCopy = unmanagedStates.some((s) => s.protection?.backupCopy);
    const bcEl = document.getElementById('backup-copy-required');
    if (bcEl) bcEl.checked = hasBackupCopy;

    // Replication → replicationRequiredToggle
    const hasReplication = unmanagedStates.some((s) => s.protection?.replication);
    if (replicationRequiredToggle) replicationRequiredToggle.checked = hasReplication;

    // CDP Replication (unmanaged matrix + existing managed jobs) → cdpReplicationRequiredToggle
    const hasCdpFromMatrix  = unmanagedStates.some((s) => s.protection?.cdpReplication);
    const hasCdpFromJobs    = sites.flatMap((s) => Array.isArray(s.jobs) ? s.jobs : []).some((j) => j.jobType === 'cdp-replication');
    if (cdpReplicationRequiredToggle) cdpReplicationRequiredToggle.checked = hasCdpFromMatrix || hasCdpFromJobs;

    // Verification → sureBackupRequiredInput (yes/no)
    const hasVerification = unmanagedStates.some((s) => s.protection?.verification);
    if (sureBackupRequiredInput) sureBackupRequiredInput.value = hasVerification ? 'yes' : 'no';

    // Tape — removed from matrix; always force off
    if (tapeIntegrationRequiredToggle) tapeIntegrationRequiredToggle.checked = false;

    // VONE (Operations) → veeam-one-required + sub-option checkboxes
    // Out-of-box puts Veeam ONE in scope (base deployment hours only); the three
    // custom options each map to a veeam-one-* sub-option that adds extra effort.
    const hasOutOfBox          = unmanagedStates.some((s) => s.operations?.outOfBox);
    const hasCustomReports     = unmanagedStates.some((s) => s.operations?.customReports);
    const hasCustomAlarms      = unmanagedStates.some((s) => s.operations?.customAlarms);
    const hasCustomDashboards  = unmanagedStates.some((s) => s.operations?.customDashboards);
    const hasVone = hasOutOfBox || hasCustomReports || hasCustomAlarms || hasCustomDashboards;
    if (veeamOneRequiredToggle) veeamOneRequiredToggle.checked = hasVone;
    const voneReportingEl   = document.getElementById('veeam-one-reporting');
    const voneAlarmsEl      = document.getElementById('veeam-one-alarms');
    const voneDashboardsEl  = document.getElementById('veeam-one-dashboards');
    if (voneReportingEl)   voneReportingEl.checked   = hasCustomReports;
    if (voneAlarmsEl)      voneAlarmsEl.checked      = hasCustomAlarms;
    if (voneDashboardsEl)  voneDashboardsEl.checked  = hasCustomDashboards;

    // Health Check (Assessment — managed + components groups) → upgradeHealthCheckReviewRequiredToggle
    // For VBR, prefer per-instance state (from expanded sub-rows) over type-level when available.
    const _vbrRows = collectUpgradeVbrRows();
    const _hasAnyPerInstVbr = _vbrRows.some((r) => upgradeScope.perVbr[r.id] !== undefined);
    const _vbrHcActive = _vbrRows.length > 0 && (
        _hasAnyPerInstVbr
            ? _vbrRows.some((r) => upgradeScope.perVbr[r.id]?.hc === true)
            : step2MatrixState.components['vbr']?.assessment?.hc === true
    );
    // Also check serverManaged blocks — both VBR (perVbr) and non-VBR (_self)
    // For VBR: also check per-workload-row HC in serverManaged (user can tick HC on a
    // workload row independently when the VBR self-row HC is not checked).
    const anyServerHC = (() => {
        const allSiteServers = sites.flatMap((site) =>
            Array.isArray(site.veeamServers) ? site.veeamServers : []
        );
        return allSiteServers.some((server) => {
            const id = server.instanceId;
            if (server.type === 'vbr') {
                if (upgradeScope.perVbr[id]?.hc === true) return true;
                // Per-workload HC rows under this VBR server
                const sm = step2MatrixState.serverManaged[id];
                return sm && Object.entries(sm)
                    .filter(([k]) => k !== '_self')
                    .some(([, s]) => s?.assessment?.hc === true);
            }
            return step2MatrixState.serverManaged[id]?._self?.assessment?.hc === true;
        });
    })();
    const anyHC = Object.values(step2MatrixState.managed).some((s) => s.assessment?.hc)
               || Object.entries(step2MatrixState.components).filter(([k]) => k !== 'vbr').some(([, s]) => s.assessment?.hc)
               || _vbrHcActive
               || anyServerHC;
    if (upgradeHealthCheckReviewRequiredToggle) {
        upgradeHealthCheckReviewRequiredToggle.checked = anyHC;
    }

    // Refresh dependent visuals
    if (typeof window.syncReplicationFields === 'function') window.syncReplicationFields();
    syncProtectionTileVisuals();
}

// ── End Scope Matrix ──────────────────────────────────────────────────────────

export { renderScopeMatrix, syncMatrixToHiddenInputs, step2MatrixState, isMatrixCellNA };
